import { TherapyTimeInterval, SignalViewer } from '@lmt/signal-viewer-ui';
import { ReportChannels } from './report-config';
import { ParsedEdfInfo } from '@lmt/signal-viewer-processor';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { buildDemoUploadData, DEMO_FILES } from '../../utils/demo-files';
import { normalizeUpload } from '../../utils/normalize-upload';
import {
  getBackendEvents,
  getGlobalEventsForGeneration,
} from '../../utils/events-helper';
import { getStartOfDayMs } from '../../utils/date-helper';
import { useReadQueryParamsFromLocation } from '../../utils/query-helper';

export const ReportChartsView: React.FC = () => {
  const [selectedDayMs, setSelectedDayMs] = useState<number | null>(null);
  const [parsedEdfInfo, setParsedEdfInfo] = useState<ParsedEdfInfo[] | null>(
    null,
  );
  const [therapyDateTimeInterval, setTherapyDateTimeInterval] =
    useState<TherapyTimeInterval>(TherapyTimeInterval.TherapyDay);

  useEffect(() => {
    if (parsedEdfInfo && parsedEdfInfo[0]) {
      setSelectedDayMs(getStartOfDayMs(parsedEdfInfo[0].startMs));
    }
  }, [parsedEdfInfo]);

  // The backend addresses the demo file to render by its DEMO_FILES index.
  const { initialIndex, timeIntervalFromQuery } =
    useReadQueryParamsFromLocation();
  const demoEntry = DEMO_FILES[initialIndex] ?? DEMO_FILES[0];

  useEffect(() => {
    if (timeIntervalFromQuery) {
      setTherapyDateTimeInterval(timeIntervalFromQuery);
    }
  }, [timeIntervalFromQuery]);

  const { data: therapyData, isLoading } = useQuery({
    queryKey: ['reportDemoFile', demoEntry.id],
    queryFn: async () => {
      const uploadedData = await buildDemoUploadData(demoEntry);
      const { therapyData, edfManifest } = await normalizeUpload(uploadedData);
      const backendEvents = await getBackendEvents(uploadedData, edfManifest);

      return {
        ...therapyData,
        events: backendEvents ?? therapyData.events,
      };
    },
  });

  return (
    <div>
      {isLoading || !therapyData ? (
        <span>Loading...</span>
      ) : (
        <SignalViewer
          selectedDayMs={selectedDayMs}
          providedConfigs={ReportChannels}
          globalEvents={getGlobalEventsForGeneration(therapyData.generation)}
          data={therapyData}
          size={{ width: 1000, height: 600 }}
          therapyTimeInterval={therapyDateTimeInterval}
          onSuccess={setParsedEdfInfo}
          onChannelDelete={() => undefined}
          containerClassName='signal-viewer__charts'
        />
      )}
    </div>
  );
};
